import { useEffect, useState } from "react";
import { api, errMsg, fmtDate, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Stamp, EmptyState, Avatar } from "@/components/common";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export function OrgAttendance() {
  const { user } = useAuth();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/attendance", { params: { date } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, [date, user.active_org_id]);
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Time & Attendance" title="Attendance" description="Who was in, when they arrived and how long they stayed." actions={<Input type="date" data-testid="attendance-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44 bg-card" />} />
      <div className="paper-card overflow-hidden rise" data-testid="org-attendance-list">
        <div className="px-4 py-3 border-b border-border bg-secondary/40 flex justify-between text-sm"><span className="font-serif font-medium">{fmtDate(date)}</span><span className="font-mono text-xs text-muted-foreground">{rows.length} present</span></div>
        {rows.length === 0 ? <EmptyState title="No records for this day" /> : rows.map((r) => (
          <div key={r.id} className="ledger-row" style={{ gridTemplateColumns: "auto minmax(0,2fr) auto 1fr 1fr 1fr auto" }} data-testid={`org-attendance-row-${r.id}`}>
            <Avatar src={r.avatar} name={r.user_name} size={30} />
            <div><div className="text-sm font-medium">{r.user_name}</div><div className="text-xs text-muted-foreground">{r.department}</div></div>
            <span data-testid={`org-attendance-slot-${r.id}`} className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary border border-border">{r.slot ? `S${r.slot} · ${r.slot_time}` : "—"}</span>
            <div className="font-mono text-xs">in {fmtTime(r.check_in)}{r.late_minutes > 0 && <span className="text-amber ml-1">+{r.late_minutes}m</span>}</div><div className="font-mono text-xs">out {fmtTime(r.check_out)}{r.completion_label && <span className="text-muted-foreground ml-1">· {r.completion_label}</span>}</div><div className="font-mono text-xs">{r.hours ? `${r.hours}h` : "—"}{r.break_minutes ? ` · ${r.break_minutes}m break` : ""} · {r.source}</div>
            <Stamp status={r.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AuditLogs() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/audit-logs").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, [user.active_org_id]);
  const shown = rows.filter((r) => !q || `${r.actor_name} ${r.action} ${r.entity} ${r.detail}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Administration" title="Audit logs" description="A chronological record of who did what." actions={<Input data-testid="audit-search-input" placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-56 bg-card" />} />
      <div className="paper-card overflow-hidden rise" data-testid="audit-list">
        {shown.length === 0 ? <EmptyState /> : shown.map((a) => (
          <div key={a.id} className="ledger-row" style={{ gridTemplateColumns: "140px auto minmax(0,1.5fr) minmax(0,2fr)" }} data-testid={`audit-row-${a.id}`}>
            <div className="font-mono text-[11px] text-muted-foreground">{fmtDate(a.created_at)} {fmtTime(a.created_at)}</div>
            <Avatar name={a.actor_name} size={26} />
            <div className="text-sm min-w-0"><span className="font-medium">{a.actor_name}</span> <span className="font-mono text-[10px] uppercase text-muted-foreground">{a.actor_role}</span></div>
            <div className="text-sm min-w-0 truncate"><span className="font-mono text-[11px] text-primary mr-2">{a.action}</span><span className="text-muted-foreground">{a.entity}</span> {a.detail && <span>· {a.detail}</span>}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
